import { useMemo, useState } from "react";

import {
  PolicyAssetClass,
  TargetPolicyRowInfo as RowInfo,
  TargetPolicyAssetSave,
  TargetPolicyItem,
  TargetPolicySaveRequest,
  TargetPolicyValues
} from "store/types";
import { toFiniteNumber } from "utils/strings";

export const EPSILON = 0.05;

export const round1 = (n: number) => Math.round(n * 10) / 10;

export const formatSigned = (n: number) => {
  const r = round1(n);
  if (r > 0) return `+${r.toFixed(1)}%`;
  if (r < 0) return `−${Math.abs(r).toFixed(1)}%`;
  return "0.0%";
};

// Composite keys disambiguate colliding system/custom ids. Asset-class rows are
// keyed by `<type>:<id>`, sub-asset-class rows by `sub:<id>`.
export const acKey = (type: string, id: number | string) => `${type}:${id}`;
export const subKey = (subId: number | string) => `sub:${subId}`;

// Flatten a target-policy list (API shape) into the composite-keyed draft map.
export const buildPolicyMap = (items: TargetPolicyItem[]): TargetPolicyValues => {
  const map: TargetPolicyValues = {};
  items.forEach((it) => {
    if (it.sub_asset_class_id === null) map[acKey(it.asset_class_type, it.asset_class_id)] = it.percentage;
    else map[subKey(it.sub_asset_class_id)] = it.percentage;
  });
  return map;
};

const cap100 = (n: number) => (n > 100 ? 100 : n < 0 ? 0 : n);

const fmt = (n: number) => n.toFixed(2);

type SubClass = PolicyAssetClass["subClasses"][number];

// Split `target` (a percentage) across `weights`, rounding each part to one decimal so that every
// part is >= 0 and the parts sum EXACTLY to round1(target). Uses the largest-remainder method in
// tenths-of-a-percent to avoid floating-point drift: each part gets its floored ideal share and the
// leftover tenths go to the parts with the largest fractional remainder (ties resolved in order, so
// e.g. 100 across 3 → 33.4 / 33.3 / 33.3). When all weights are zero the target is split equally.
//
// The naive "first part absorbs the remainder" approach is unsafe: when `target` is small relative
// to the number of parts, rounding each base share UP can make the remainder negative, and that
// negative part is later dropped by buildResult's `> 0` filter — breaking the sum invariant the
// backend enforces ("sub-asset percentages must equal the asset class percentage").
const distribute = (target: number, weights: number[]): number[] => {
  const n = weights.length;
  if (!n) return [];
  const totalTenths = Math.round(round1(target) * 10);
  if (totalTenths <= 0) return weights.map(() => 0);
  const totalW = weights.reduce((sum, w) => sum + (w > 0 ? w : 0), 0);
  const effective = totalW > 0 ? weights.map((w) => (w > 0 ? w : 0)) : weights.map(() => 1);
  const effTotal = totalW > 0 ? totalW : n;
  const ideal = effective.map((w) => (w / effTotal) * totalTenths);
  const tenths = ideal.map((x) => Math.floor(x));
  const remainder = totalTenths - tenths.reduce((sum, t) => sum + t, 0);
  const order = ideal.map((x, i) => ({ i, frac: x - Math.floor(x) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; k < remainder; k++) tenths[order[k].i] += 1;
  return tenths.map((t) => t / 10);
};

// Distribute `target` across `subs` proportionally to their previous weights, rounding so the
// sub-total lands exactly on `target`. No-op when there are no subs or they are all currently zero.
const rescaleSubs = (next: TargetPolicyValues, prev: TargetPolicyValues, subs: SubClass[], target: number) => {
  const weights = subs.map((s) => toFiniteNumber(prev[subKey(s.id)]));
  const prevTotal = weights.reduce((sum, w) => sum + w, 0);
  if (subs.length === 0 || prevTotal <= 0) return;
  const parts = distribute(target, weights);
  subs.forEach((s, i) => {
    next[subKey(s.id)] = fmt(parts[i]);
  });
};

const buildSeed = (classes: PolicyAssetClass[], initial?: TargetPolicyItem[]): TargetPolicyValues => {
  const initialMap = buildPolicyMap(initial ?? []);
  const seed: TargetPolicyValues = {};
  classes.forEach((p) => {
    const pid = acKey(p.type, p.id);
    seed[pid] = initialMap[pid] ?? fmt(0);
    p.subClasses.forEach((s) => {
      const sid = subKey(s.id);
      seed[sid] = initialMap[sid] ?? fmt(0);
    });
  });
  return seed;
};

export const useTargetPolicyState = (assetClasses: PolicyAssetClass[], initialPolicy?: TargetPolicyItem[]) => {
  const seeded = useMemo(() => buildSeed(assetClasses, initialPolicy), [assetClasses, initialPolicy]);

  const [draft, setDraft] = useState<TargetPolicyValues>(seeded);
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(assetClasses.map((p) => [acKey(p.type, p.id), false]))
  );

  const parentSum = useMemo(
    () => assetClasses.reduce((s, p) => s + toFiniteNumber(draft[acKey(p.type, p.id)]), 0),
    [assetClasses, draft]
  );

  const unallocated = round1(100 - parentSum);
  const overAllocated = unallocated < -EPSILON;
  const canNormalize = parentSum > 0 && Math.abs(parentSum - 100) > EPSILON;

  // While typing, only the parent value changes — sub-classes are rescaled on blur.
  const setParent = (key: string, raw: string | number) => {
    const newParent = cap100(round1(typeof raw === "number" ? raw : Number(raw) || 0));
    setDraft((prev) => ({ ...prev, [key]: fmt(newParent) }));
  };

  // On blur, rescale the sub-classes proportionally so their sub-total matches the parent
  // (backend requires sub-asset percentages to sum to the asset class percentage).
  const commitParent = (key: string) => {
    const parent = assetClasses.find((p) => acKey(p.type, p.id) === key);
    const subs = parent?.subClasses ?? [];
    if (subs.length === 0) return;
    setDraft((prev) => {
      const next = { ...prev };
      rescaleSubs(next, prev, subs, toFiniteNumber(prev[key]));
      return next;
    });
  };

  // While typing, only the sub-class value changes — the parent is left untouched.
  const setSub = (key: string, raw: string | number) => {
    const newValue = cap100(round1(typeof raw === "number" ? raw : Number(raw) || 0));
    setDraft((prev) => ({ ...prev, [key]: fmt(newValue) }));
  };

  // On blur: keep the edited sub's value and let the parent follow the new sub-total.
  // If the sub-total would exceed 100, cap the parent at 100 and reduce the OTHER subs
  // proportionally so the sub-total still equals the parent.
  const commitSub = (key: string) => {
    const parent = assetClasses.find((p) => p.subClasses.some((s) => subKey(s.id) === key));
    if (!parent) return;
    const pid = acKey(parent.type, parent.id);
    const others = parent.subClasses.filter((s) => subKey(s.id) !== key);
    setDraft((prev) => {
      const editedVal = toFiniteNumber(prev[key]);
      const otherTotal = others.reduce((sum, s) => sum + toFiniteNumber(prev[subKey(s.id)]), 0);
      const next = { ...prev };
      if (editedVal + otherTotal <= 100) {
        next[pid] = fmt(round1(editedVal + otherTotal));
      } else {
        rescaleSubs(next, prev, others, round1(100 - editedVal));
        next[pid] = fmt(100);
      }
      return next;
    });
  };

  const toggleExpanded = (key: string) => {
    setExpandedById((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Scale each parent to 100% proportionally (parts sum to exactly 100), and rescale its
  // sub-classes to match the new parent.
  const handleNormalize = () => {
    if (!canNormalize) return;
    setDraft((prev) => {
      const next = { ...prev };
      const parentShares = distribute(
        100,
        assetClasses.map((p) => toFiniteNumber(prev[acKey(p.type, p.id)]))
      );
      assetClasses.forEach((p, idx) => {
        const pid = acKey(p.type, p.id);
        const newParent = parentShares[idx];
        next[pid] = fmt(newParent);
        rescaleSubs(next, prev, p.subClasses ?? [], newParent);
      });
      return next;
    });
  };

  // Split 100% equally across asset classes, then split each asset class's share equally across its
  // sub-classes. distribute() guarantees every share is >= 0 and sub-totals land exactly on the
  // parent, so the backend's sub-asset sum check never fails.
  const handleEqualSplit = () => {
    const n = assetClasses.length;
    if (n === 0) return;
    const parentShares = distribute(
      100,
      assetClasses.map(() => 1)
    );
    const next: TargetPolicyValues = {};
    assetClasses.forEach((p, idx) => {
      const share = parentShares[idx];
      next[acKey(p.type, p.id)] = fmt(share);
      const subs = p.subClasses ?? [];
      const subShares = distribute(
        share,
        subs.map(() => 1)
      );
      subs.forEach((s, subIdx) => {
        next[subKey(s.id)] = fmt(subShares[subIdx]);
      });
    });
    setDraft(next);
  };

  const getParent = (key: string): number => toFiniteNumber(draft[key]);
  const getSub = (key: string): number => toFiniteNumber(draft[key]);

  const getRowInfo = (parent: PolicyAssetClass): RowInfo => {
    const subs = parent.subClasses;
    const parentTarget = toFiniteNumber(draft[acKey(parent.type, parent.id)]);
    const subTotal = subs.reduce((s, sub) => s + toFiniteNumber(draft[subKey(sub.id)]), 0);
    const anySubEntered = subs.some((s) => toFiniteNumber(draft[subKey(s.id)]) !== 0);
    const subTotalMismatch = anySubEntered && Math.abs(round1(subTotal) - round1(parentTarget)) > EPSILON;
    const arrow: RowInfo["arrow"] = subTotalMismatch ? (subTotal > parentTarget ? "↑" : "↓") : "";
    return { parentTarget, subTotal, subTotalMismatch, arrow };
  };

  const buildResult = (): TargetPolicySaveRequest =>
    assetClasses
      .map((p): TargetPolicyAssetSave => {
        const percentage = round1(toFiniteNumber(draft[acKey(p.type, p.id)]));
        // Custom asset classes are flat — never send sub_assets for them.
        if (p.type === "custom") {
          return { asset_class_id: Number(p.id), asset_class_type: "custom", percentage };
        }
        return {
          asset_class_id: Number(p.id),
          asset_class_type: "system",
          percentage,
          sub_assets: p.subClasses
            .map((s) => ({ sub_asset_class_id: Number(s.id), percentage: round1(toFiniteNumber(draft[subKey(s.id)])) }))
            .filter((s) => s.percentage > 0)
        };
      })
      .filter((row) => row.percentage > 0);

  return {
    draft,
    expandedById,
    unallocated,
    overAllocated,
    canNormalize,
    setParent,
    commitParent,
    setSub,
    commitSub,
    toggleExpanded,
    handleNormalize,
    handleEqualSplit,
    getParent,
    getSub,
    getRowInfo,
    buildResult
  };
};
